import type { Artifact } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { downloadBlob } from 'utils/download'

import {
  type ArtifactApiClient,
  artifactDownloadsUrl,
  artifactUrl,
  fetchArtifact,
  isArtifactRevisionConflict,
  postArtifactDownload,
} from './artifactApi'
import { putArtifact } from './artifactStore'
import { artifactFilename } from './artifactText'
import { artifactRendering } from './kinds'

export const ARTIFACT_CHANGED_WHILE_RENDERING =
  'This document changed while rendering; download again.'

/**
 * Renders the kind's file, records the download for the rendered revision,
 * and only then hands the file to the browser. A 409 means the artifact moved
 * on while rendering: the newer copy is fetched into the store and nothing is saved.
 */
export async function downloadArtifact({
  artifact,
  axios,
  powerBackendUrl,
  render = artifactRendering(artifact).download.render,
  save = downloadBlob,
}: {
  artifact: Artifact
  axios: ArtifactApiClient
  powerBackendUrl: string
  render?: () => Promise<Blob>
  save?: (blob: Blob, filename: string) => void
}): Promise<Artifact> {
  const { mimeType } = artifactRendering(artifact).download
  const file = new Blob([await render()], { type: mimeType })
  const filename = artifactFilename(artifact)
  let recorded: Artifact
  try {
    recorded = await postArtifactDownload(
      axios,
      artifactDownloadsUrl(powerBackendUrl, artifact.artifact_id),
      { siteId: artifact.site_id, revision: artifact.revision, file, filename }
    )
  } catch (error) {
    if (!isArtifactRevisionConflict(error)) throw error
    putArtifact(
      await fetchArtifact(
        axios,
        artifactUrl(powerBackendUrl, artifact.artifact_id, artifact.site_id)
      )
    )
    throw new Error(ARTIFACT_CHANGED_WHILE_RENDERING)
  }
  save(file, filename)
  return putArtifact(recorded)
}
